import { takeLatest, put, call, select } from 'redux-saga/effects';
import get from 'lodash/get';
import drop from 'lodash/drop';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import time from 'yoda-core-components/lib/moment/Time';
import {
    getOrderListFromV3,
    getOrderList as getOrderListFromV1,
} from 'yoda-interfaces/lib/Account/AccountApi';
import FSaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { doPostSSRLogs } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { selectContext } from 'yoda-site-components/lib/selectors/ContextSelector';
import { HIDE_CANCEL_ORDER_NOTIFICATION } from 'yoda-account-components/lib/actionTypes/CancelOrderActionTypes';
import { GET_MY_ORDERS } from '../actionTypes/MyOrdersActionTypes';
import ApplicationLogger from '../helpers/ApplicationLogger';
import { selectOrdersFromV3ImpulseThrottleFF } from '../selectors/ordersV3ImpulseSelector';
import { getFilterQuery } from '../helpers/Utils';

const context = (state) => state.context;
const ordersHistoryState = (state) => state.ordersHistory;

function* myOrdersSaga(actionParam) {
    const action = { ...actionParam };
    const logger = ApplicationLogger.getLogger();
    const cookies = get(action, 'headers.cookies', {});
    let ssrData = {};
    let ssrSuccessful = false;

    try {
        yield put({ type: HIDE_CANCEL_ORDER_NOTIFICATION });
        const appContext = yield select(context);
        const enableOrdersFilterRedesign = get(
            appContext,
            'featureFlags.enableOrdersFilterRedesign',
            true
        );
        const ordersFilterOptions = enableOrdersFilterRedesign
            ? get(appContext, 'preferences.NewOrdersFilterOptions', {})
            : get(appContext, 'preferences.ordersFilterOptions', {});
        const fetchLastSupportedYears = get(appContext, 'preferences.fetchLastSupportedYears', 4);
        let orderResponse = {};

        const filters = action.payload;
        const orderHistoryState = yield select(ordersHistoryState);
        const cashedOrderHistory = get(orderHistoryState, 'cachedCall');
        const { allowToUseCachedOrder } = filters;
        const useCached = allowToUseCachedOrder && cashedOrderHistory;
        const getOrdersListFromV3Impulse = yield select(selectOrdersFromV3ImpulseThrottleFF);
        const getOrderList = getOrdersListFromV3Impulse ? getOrderListFromV3 : getOrderListFromV1;

        let ordersHistory;
        if (useCached) {
            ordersHistory = cashedOrderHistory;
        } else {
            const { filter } = action.payload;
            let dateFrom =
                filter === 'older'
                    ? time().subtract(13, 'months').add(1, 'day')
                    : time().subtract(filter, 'days');
            let dateTo = filter === 'older' ? time().subtract(91, 'days') : time();
            if (enableOrdersFilterRedesign) {
                const result = getFilterQuery(fetchLastSupportedYears, filter);
                [dateFrom, dateTo] = result;
            }
            action.payload.dateFrom = dateFrom.format('YYYY-MM-DD');
            action.payload.dateTo = dateTo.format('YYYY-MM-DD');
            ordersHistory = yield call(FSaga, getOrderList, action, undefined, false);
        }
        orderResponse = get(ordersHistory, 'response', {});

        if (orderResponse && (orderResponse.status === 200 || orderResponse.status === 404)) {
            const ordersHistoryClone = cloneDeep(ordersHistory);
            yield put({ type: 'GET_ORDERS_SUCCESS', ordersHistory });
            if (orderResponse.data) {
                ordersHistoryClone.response.data.filters = filters;
                const data = orderResponse.status === 404 ? [] : ordersHistoryClone.response.data;
                const totalOrders = data.length;

                // sorting
                const sortedOrders = orderBy(data, ['date'], [filters.orderByDate]);

                // paging
                const offset = (filters.page - 1) * filters.limit;
                const offsetOrders = drop(sortedOrders, offset);
                const paginatedOrders = offsetOrders.slice(0, filters.limit);

                ordersHistoryClone.response.data.filterDates = ordersFilterOptions;
                ordersHistoryClone.response.data.limit = filters.limit;
                ordersHistoryClone.response.data.pages = Math.ceil(totalOrders / filters.limit);
                ordersHistoryClone.response.data.page = filters.page;
                ordersHistoryClone.response.data.totalOrders = totalOrders;
                ordersHistoryClone.response.data.orders = paginatedOrders;
            }
            yield put({ type: 'GET_MY_ORDERS_SUCCESS', ordersHistory: ordersHistoryClone });
        } else {
            // Add OM_GENERIC_ERROR as a fallback to show Generic ErrorMessage in orderList Page
            const error = get(orderResponse, 'data[0]', { errorCode: 'OM_GENERIC_ERROR' });
            // Let status 401 with SRV_SESSION_INVALID be handled by factory saga thus don't update redux yet
            if (orderResponse.status !== 401 && error.errorCode !== 'SRV_SESSION_INVALID') {
                yield put({ type: 'GET_MY_ORDERS_ERROR', error });
            }
        }

        ssrData = orderResponse;
        ssrSuccessful = orderResponse.status === 200 || orderResponse.status === 404;
    } catch (error) {
        const errorPayload = {
            errorMessage: error.message,
            errorCode: 'EXCEPTION_IN_MY_ORDERS_SAGA',
        };
        yield put({
            type: 'GET_MY_ORDERS_ERROR',
            error: errorPayload,
        });
        ssrData.data = errorPayload;
    }
    const { cluster } = yield select(selectContext);
    __SERVER__ &&
        doPostSSRLogs(
            logger,
            {
                requestInfo: `IS MY ORDER PAGE RENDERED ON SERVER SIDE : ${ssrSuccessful}`,
                response: ssrData,
            },
            cookies,
            cluster
        );
}

function* watchMyOrdersSaga() {
    yield takeLatest(GET_MY_ORDERS, myOrdersSaga);
}

watchMyOrdersSaga.sagaName = 'myOrdersSaga';
export default watchMyOrdersSaga;
